import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DNAVISIT_CONSENT, MINOR_CONSENT, MINOR_CONSENT_CODE } from '@pages/order/constants/consents';
import { fetchAllConsents } from '@store/actions';

import type { Consent, WizardStep } from '../types/Wizard.types';

const HIE_CONSENT_PREFIX = 'myome:consent:HIE_ACCESS__TREATMENT';
const RESEARCH_CONSENT_PREFIX = 'myome:consent:CONTACT__RESEARCH_OPPORTUNITIES';
const TRANSACTIONAL_SMS_CONSENT_PREFIX = 'myome:consent:CONTACT__TRANSACTIONAL_SMS';
const MARKETING_CONSENT_PREFIX = 'myome:consent:CONTACT__MARKETING';

interface UseWizardConsentsProps {
  products: Array<{ product_id?: string }>;
  steps: readonly WizardStep[];
  currentStep: number;
  // Gated minor consent: only fetched for minor orders (BridgeBio + DOB < 18).
  includeMinorConsent?: boolean;
  // HIE is optional and pre-checked for most campaigns; omit for BridgeBio.
  includeHIEConsent?: boolean;
  // Research contact is optional and pre-checked for most campaigns; omit for BridgeBio.
  includeResearchConsent?: boolean;
  // Called when consents are discarded because the product selection changed.
  onProductsChanged?: () => void;
}

interface UseWizardConsentsReturn {
  allConsents: Consent[];
  dnaVisitConsent: Consent | null;
  hieConsent: Consent | null;
  researchConsent: Consent | null;
  transactionalSmsConsent: Consent | null;
  marketingConsent: Consent | null;
  minorConsent: Consent | null;
  loadingConsents: boolean;
  consentsFailed: boolean;
  fetchConsentsData: () => void;
}

/**
 * Hook that manages consents fetching and state.
 * Automatically fetches consents when user reaches the consents step.
 */
export function useWizardConsents({
  products,
  steps,
  currentStep,
  includeMinorConsent = false,
  includeHIEConsent = true,
  includeResearchConsent = true,
  onProductsChanged,
}: UseWizardConsentsProps): UseWizardConsentsReturn {
  const [allConsents, setAllConsents] = useState<Consent[]>([]);
  const [dnaVisitConsent, setDnaVisitConsent] = useState<Consent | null>(null);
  const [hieConsent, setHieConsent] = useState<Consent | null>(null);
  const [researchConsent, setResearchConsent] = useState<Consent | null>(null);
  const [transactionalSmsConsent, setTransactionalSmsConsent] = useState<Consent | null>(null);
  const [marketingConsent, setMarketingConsent] = useState<Consent | null>(null);
  const [minorConsent, setMinorConsent] = useState<Consent | null>(null);
  const [loadingConsents, setLoadingConsents] = useState(false);
  const [consentsFailed, setConsentsFailed] = useState(false);

  // Identifies the product set the currently loaded consents belong to.
  const productIdsKey = useMemo(
    () =>
      (products || [])
        .map(p => p.product_id)
        .filter((id): id is string => !!id)
        .sort()
        .join(','),
    [products],
  );
  const attemptedKeyRef = useRef<string | null>(null);
  const previousProductIdsKeyRef = useRef(productIdsKey);
  const requestIdRef = useRef(0);
  const onProductsChangedRef = useRef(onProductsChanged);

  useEffect(() => {
    onProductsChangedRef.current = onProductsChanged;
  });

  const clearConsents = useCallback(() => {
    setAllConsents([]);
    setDnaVisitConsent(null);
    setHieConsent(null);
    setResearchConsent(null);
    setTransactionalSmsConsent(null);
    setMarketingConsent(null);
    setMinorConsent(null);
  }, []);

  const fetchConsentsData = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoadingConsents(true);
    setConsentsFailed(false);
    attemptedKeyRef.current = productIdsKey;

    const productIds = productIdsKey.split(',').filter(Boolean);
    const consentsToFetch = [
      'DNAVISIT',
      'GENERAL',
      ...(includeHIEConsent ? ['HIE_ACCESS__TREATMENT'] : []),
      ...(includeResearchConsent ? ['CONTACT__RESEARCH_OPPORTUNITIES'] : []),
      'CONTACT__TRANSACTIONAL_SMS',
      'CONTACT__MARKETING',
      ...(includeMinorConsent ? [MINOR_CONSENT_CODE] : []),
      ...productIds,
    ];
    fetchAllConsents(consentsToFetch)
      .then((result: unknown) => {
        if (requestId !== requestIdRef.current) return;
        if (!Array.isArray(result)) {
          setConsentsFailed(true);
          clearConsents();
          return;
        }
        const arr = result as Array<{ consent_uri?: string; consent_text?: string }>;
        const toConsent = (c: { consent_uri?: string; consent_text?: string }): Consent => ({
          consent_uri: c.consent_uri || '',
          consent_text: c.consent_text || '',
        });

        const remaining: Consent[] = [];
        let dna: Consent | null = null;
        let hie: Consent | null = null;
        let research: Consent | null = null;
        let transactionalSms: Consent | null = null;
        let marketing: Consent | null = null;
        let minor: Consent | null = null;

        for (const c of arr) {
          if (c.consent_uri?.startsWith(DNAVISIT_CONSENT)) {
            dna = toConsent(c);
          } else if (c.consent_uri?.startsWith(MINOR_CONSENT)) {
            minor = toConsent(c);
          } else if (c.consent_uri?.startsWith(HIE_CONSENT_PREFIX)) {
            hie = toConsent(c);
          } else if (c.consent_uri?.startsWith(RESEARCH_CONSENT_PREFIX)) {
            research = toConsent(c);
          } else if (c.consent_uri?.startsWith(TRANSACTIONAL_SMS_CONSENT_PREFIX)) {
            transactionalSms = toConsent(c);
          } else if (c.consent_uri?.startsWith(MARKETING_CONSENT_PREFIX)) {
            marketing = toConsent(c);
          } else {
            remaining.push(toConsent(c));
          }
        }

        setDnaVisitConsent(dna);
        setHieConsent(hie);
        setResearchConsent(research);
        setTransactionalSmsConsent(transactionalSms);
        setMarketingConsent(marketing);
        setMinorConsent(minor);
        setAllConsents(remaining);
        if (!remaining.length && !dna) {
          setConsentsFailed(true);
        }
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setConsentsFailed(true);
        clearConsents();
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoadingConsents(false);
      });
  }, [productIdsKey, includeMinorConsent, includeHIEConsent, includeResearchConsent, clearConsents]);

  // Every selection change invalidates both prior consent text and in-flight requests.
  useEffect(() => {
    if (previousProductIdsKeyRef.current === productIdsKey) return;
    previousProductIdsKeyRef.current = productIdsKey;
    requestIdRef.current += 1;
    attemptedKeyRef.current = null;
    clearConsents();
    setLoadingConsents(false);
    setConsentsFailed(false);
    onProductsChangedRef.current?.();
  }, [productIdsKey, clearConsents]);

  useEffect(() => {
    if (steps[currentStep] !== 'consents') return;
    if (loadingConsents) return;
    // A failed attempt keeps the key so it does not retry in a loop; the
    // consents step exposes an explicit retry through fetchConsentsData.
    if (attemptedKeyRef.current === productIdsKey) return;
    fetchConsentsData();
  }, [currentStep, steps, productIdsKey, loadingConsents, fetchConsentsData]);

  return {
    allConsents,
    dnaVisitConsent,
    hieConsent,
    researchConsent,
    transactionalSmsConsent,
    marketingConsent,
    minorConsent,
    loadingConsents,
    consentsFailed,
    fetchConsentsData,
  };
}
